import React, { useState, useEffect } from "react";
import { useTheme } from "styled-components";
import { Pie } from "react-chartjs-2";
import { ReactComponent as FingerPointingIcon } from "assets/images/finger-pointing.svg";
import { ReactComponent as InfoIcon } from "assets/images/info-icon.svg";
import { ReactComponent as InfoIcon2 } from "assets/images/info-icon-2.svg";
import { ReactComponent as CircleChevronIcon } from "assets/images/circle-chevron.svg";
import SubjectRecommendations from "components/molecules/SubjectRecommendations";
import { useGetDashboardQuery, useViewDashboardQuery } from "generated/graphql";
import ReactTooltip from "react-tooltip";
import { MD } from "constants/devices";
import { useHistory, useParams } from "react-router-dom";
import {
  LinkItem,
  LinksList,
  CountryHeader,
  CountryName,
  CountrySection,
  SideCard,
  SideCardsContainer,
  SideCardTitle,
  SideCardWrapper,
  Divider,
  LinkText,
} from "pages/Student/CareerDetails/styles";
import Button from "components/atoms/Button";
import * as S from "./styles";

const DashboardViewer = () => {
  const theme = useTheme();

  const history = useHistory();
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");
  const { testAttemptId } = useParams<{ testAttemptId: string }>();

  const { data: dashboardDataAccess } = useViewDashboardQuery({
    variables: { token: token || "" },
  });

  const { data: dashboardDataRegular } = useGetDashboardQuery({
    variables: {
      testId: Number(testAttemptId),
    },
  });

  const dashboardData =
    dashboardDataAccess?.viewDashboard || dashboardDataRegular?.getDashboard;

  const aptitudeData = [
    {
      label: "Verbal",
      value: Math.round(
        ((dashboardData?.scores?.verbal || 0) /
          (dashboardData?.scores?.cas || 0)) *
          100 || 0
      ),
      color: theme.colors.fuelYellow,
      description: {
        label: "Verbal Reasoning",
        description:
          "understanding data, information and communications presented in words and making decisions based on that understanding. A practical example would be:",
        italics:
          "“Which is the odd one out in the following list?” \nHouse | School | Hotel | Hospital | River",
      },
    },
    {
      label: "Abstract",
      value: Math.round(
        ((dashboardData?.scores?.abstract || 0) /
          (dashboardData?.scores?.cas || 0)) *
          100 || 0
      ),
      color: theme.colors.pictonBlue,
      description: {
        label: "Abstract Reasoning",
        description:
          "observing situations, experiences and events that do not require either verbal reasoning or numerical aptitude and arriving at conclusions based on that observation. A practical example would be: ",
        italics:
          "You turn on the hot tap in the kitchen and put your hand under it – what is likely to happen?",
      },
    },
    {
      label: "Numerical",
      value: Math.round(
        ((dashboardData?.scores?.numerical || 0) /
          (dashboardData?.scores?.cas || 0)) *
          100 || 0
      ),
      color: theme.colors.mulberry,
      description: {
        label: "Numerical Ability",
        description:
          "understanding data, information and communications presented in numbers, graphs, charts; understanding the logic in them and making decisions based on that understanding. A practical example would be:",
        italics:
          "“If the normal price of a pair of shoes is $200 and in a sale they are reduced by 20%- how much will they cost you in the sale?”",
      },
    },
  ];

  const careersData =
    dashboardData?.suggestedCareers?.map((item) => ({
      name: item.name,
      id: item.id,
      isFavorited: item.favorite,
      description: item.description,
    })) || [];

  // Prepare data for Chart.js pie chart
  const pieChartData = {
    labels: aptitudeData.map((item) => item.label),
    datasets: [
      {
        data: aptitudeData.map((item) => item.value),
        backgroundColor: aptitudeData.map((item) => item.color),
        borderColor: "#fff",
        borderWidth: 10,
        hoverBorderWidth: 14,
        hoverBorderColor: "#fff",
      },
    ],
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem: any, data: any) => {
          const label = data.labels[tooltipItem.index];
          const value =
            data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
          return `${label}: ${value}%`;
        },
      },
    },
  };

  const [isInterestModalOpen, setIsInterestModalOpen] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const [showOverflow, setShowOverflow] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const tooltipBackgroundColor = screenWidth <= MD ? "#073356" : "#ffffff";

  const getBarWidth = (value: number) => `${(value / 70) * 23.3}rem`;
  const getBarOpacity = (value: number) => {
    if (value >= 70) return 1;
    if (value >= 60) return 0.9;
    if (value >= 55) return 0.8;
    if (value >= 50) return 0.7;
    if (value >= 45) return 0.6;
    if (value >= 40) return 0.5;
    if (value >= 30) return 0.4;
    if (value >= 20) return 0.3;
    return 0.2;
  };

  // if (!loading && !dashboardData) {
  //   window.location.href = "/";
  // }

  return (
    <S.Container>
      <S.Heading>
        <S.HeadingText>
          Viewing dashboard of {dashboardData?.user?.firstName || ""}{" "}
          {dashboardData?.user?.lastName || ""}
        </S.HeadingText>
        <Button
          variant="primary"
          width="auto"
          onClick={() => {
            window.print();
          }}
        >
          Download Dashboard
        </Button>
      </S.Heading>

      <S.TooltipGlobalStyle />
      <S.InterestModal
        isOpen={isInterestModalOpen}
        title="Interest Inventory"
        onClose={() => setIsInterestModalOpen(false)}
      >
        <S.InterestModalContent>
          {dashboardData?.interestInventory.map((item) => (
            <S.InterestItem key={item.name}>
              <p>
                <strong>{item.name}</strong>
                {item.description}
              </p>
            </S.InterestItem>
          ))}
        </S.InterestModalContent>
      </S.InterestModal>
      <S.DashboardRow>
        {/* Aptitude Skills Card */}
        <S.AptitudeCard>
          <S.CardHeader>
            <S.CardTitle>RELATIVE APTITUDE SKILLS</S.CardTitle>
          </S.CardHeader>

          <S.AptitudeContent>
            <S.PieChartContainer>
              <Pie data={pieChartData} options={pieChartOptions} />
            </S.PieChartContainer>
            <ReactTooltip
              id="aptitude-tooltip"
              place="bottom"
              type="info"
              backgroundColor={tooltipBackgroundColor}
              textColor={
                tooltipBackgroundColor === "#073356" ? "#ffffff" : "#073356"
              }
              effect="solid"
              className="custom-tooltip"
              getContent={(content) => {
                const { label, description, italics } = JSON.parse(
                  content || "{}"
                );
                return (
                  <span>
                    <strong>{label}</strong> - {description}
                    <em style={{ marginTop: "2rem", display: "block" }}>
                      {italics}
                    </em>
                  </span>
                );
              }}
            />

            <S.LegendContainer>
              {aptitudeData.map((item) => (
                <S.LegendItem key={item.label}>
                  <S.LegendItemContent>
                    <S.LegendColorBox color={item.color} />
                    <S.LegendTextContainer>
                      <S.LegendLabel>{item.label}</S.LegendLabel>
                      <S.LegendValue>{item.value}%</S.LegendValue>
                    </S.LegendTextContainer>
                    <InfoIcon2
                      data-tip={JSON.stringify(item.description)}
                      data-for="aptitude-tooltip"
                    />
                  </S.LegendItemContent>
                </S.LegendItem>
              ))}
            </S.LegendContainer>
          </S.AptitudeContent>
        </S.AptitudeCard>

        {/* Interest Inventory Card */}
        <S.InterestCard>
          <S.CardHeader>
            <S.CardTitle>INTEREST INVENTORY</S.CardTitle>
            <S.DetailsButton onClick={() => setIsInterestModalOpen(true)}>
              <InfoIcon />
              <S.DetailsText>Details</S.DetailsText>
            </S.DetailsButton>
          </S.CardHeader>
          {dashboardData?.interestInventory && (
            <ReactTooltip
              id="interest-tooltip"
              place="bottom"
              backgroundColor={tooltipBackgroundColor}
              textColor={
                tooltipBackgroundColor === "#073356" ? "#ffffff" : "#073356"
              }
              effect="solid"
              className="custom-tooltip"
              getContent={(content) => {
                const { label, description } = JSON.parse(content || "{}");
                return (
                  <span>
                    <strong>{label}</strong>
                    {description}
                  </span>
                );
              }}
            />
          )}
          <S.InterestContent showOverflow={showOverflow}>
            <S.InterestChart>
              {dashboardData?.interestInventory.map((item) => (
                <S.InterestRow key={item.name}>
                  <S.InterestLabel>{item.name}</S.InterestLabel>
                  <S.InterestBar
                    width={getBarWidth(item.score)}
                    opacity={getBarOpacity(item.score)}
                    data-tip={JSON.stringify({
                      label: item.name,
                      description: item.description,
                    })}
                    data-for="interest-tooltip"
                  />
                  <S.InterestValue>{item.score}</S.InterestValue>
                </S.InterestRow>
              ))}
            </S.InterestChart>
          </S.InterestContent>
        </S.InterestCard>
      </S.DashboardRow>
      {/* Career Suggestions List */}
      <S.CareersCard>
        <S.CareersHeader>
          <S.CareersTitle>Suggested Careers</S.CareersTitle>
        </S.CareersHeader>

        <S.CareersList>
          {careersData.map((career, index) => (
            <React.Fragment key={career.name}>
              <S.CareerItem
                key={career.name}
                isAlternate={index % 2 === 0}
                className={`${career.isFavorited ? "favorited" : ""}`}
              >
                <S.CareerName>{career.name}</S.CareerName>
                <S.StarIcon isFavorited={career.isFavorited || false} />

                <S.LearnMoreSection
                  onClick={() => {
                    if (!token) {
                      history.push(
                        `/dashboard-preview/${testAttemptId}/careers/${career.id}`
                      );
                    } else {
                      history.push(
                        `/dashboard-access/careers/${career.id}?token=${token}`
                      );
                    }
                  }}
                >
                  <S.LearnMoreText>Learn More</S.LearnMoreText>
                  <CircleChevronIcon />
                </S.LearnMoreSection>
              </S.CareerItem>
              {career.isFavorited && (
                <S.CarrerDescription>{career.description}</S.CarrerDescription>
              )}
            </React.Fragment>
          ))}
        </S.CareersList>
      </S.CareersCard>
      {dashboardData?.user?.careerStage === "CAREER_STARTER" && (
        <SubjectRecommendations
          subjectRecommendations={dashboardData?.subjectRecommendations || []}
          allSubjects={dashboardData?.allSubjects || []}
          favouriteCareers={
            dashboardData?.suggestedCareers?.filter(
              (career) => career.favorite
            ) || []
          }
        />
      )}
      <SideCardsContainer id="helpful-links">
        <SideCard isFirst>
          <SideCardTitle>Choosing where to study</SideCardTitle>
          <SideCardWrapper>
            {dashboardData?.whereToStudy
              ?.filter((item) => item.header !== "Online Training")
              .map((item, index) => (
                <React.Fragment key={item.header}>
                  <CountrySection>
                    <CountryHeader>
                      <FingerPointingIcon />

                      <CountryName>{item.header}</CountryName>
                    </CountryHeader>
                    <LinksList>
                      {item.links.map((link) => (
                        <LinkItem href={link.url} key={link.label}>
                          <LinkText>{link.label}</LinkText>
                        </LinkItem>
                      ))}
                    </LinksList>
                  </CountrySection>
                  {index !== (dashboardData?.whereToStudy?.length || 0) - 2 && (
                    <Divider />
                  )}
                </React.Fragment>
              ))}
          </SideCardWrapper>
        </SideCard>

        <SideCard>
          <SideCardTitle>Trainings</SideCardTitle>
          {dashboardData?.whereToStudy
            ?.filter((item) => item.header === "Online Training")
            .map((item) => (
              <CountrySection style={{ width: "200px" }} key={item.header}>
                <CountryHeader>
                  <FingerPointingIcon />
                  <CountryName>{item.header}</CountryName>
                </CountryHeader>
                <LinksList>
                  {item.links.map((link) => (
                    <LinkItem href={link.url} key={link.label}>
                      <LinkText>{link.label}</LinkText>
                    </LinkItem>
                  ))}
                </LinksList>
              </CountrySection>
            ))}
        </SideCard>
      </SideCardsContainer>
    </S.Container>
  );
};

export default DashboardViewer;
